import {NodeElement, NodeElementOptions, StyleObject} from "./NodeElement";
import {PanelElement} from "./PanelElement";
import {IFrameMessages} from "../../messaging/IFrameMessages";
import {GATE_MOUNT_TYPE} from "../../Constants";
import {bodyScrollLock} from "../../utils/ScrimHelpers.js";
import {Dispatcher} from "../../../stem-core/base/Dispatcher";

// How often a mounted gate checks that it's still on the page.
const DISCARD_CHECK_INTERVAL_MS = 200;

// Fires when a gate notices it was discarded, so whoever put it there can decide to gate the new page.
export const gateDiscarded = new Dispatcher();

export interface InteractiveHandlers {
    remove: () => void;
    updatePanel: (panelOptions: Partial<NodeElementOptions>) => void;
    // True once the merchant's router took the gate off the page without us tearing it down.
    isDiscarded: () => boolean;
}

export const GateStyle = {
    gate: {
        pointerEvents: "initial",
        height: "100%",
        display: "flex"
    },
};

export class GateElement extends NodeElement {
    styleSheet = GateStyle;
    panel: PanelElement;
    parentNode: HTMLElement;
    initialParentStyle: Record<string, string>;
    // Set by the gates that occlude the viewport. Banners and previews leave the page scrollable on
    // purpose - the reader still has an article to finish, or a footer to reach.
    locksBodyScroll = false;
    // Set when the merchant's router took our node off the page. Distinct from a gate we tore down
    // ourselves: a dismissed gate has to stay gone, a discarded one has to be replaced on the new page.
    discarded = false;

    constructor(options: NodeElementOptions, panel: PanelElement) {
        super(options);
        this.panel = panel;
    }

    // An SPA router can drop our node without going through destroyNode, and then nothing releases the
    // scroll lock, frees up the container or tells the journey that mounted us that its gate is gone. So
    // we watch for it ourselves and tear down through the normal path. Returns whether we're still up.
    checkStillMounted(): boolean {
        if (this.node && !document.contains(this.node)) {
            this.discarded = true;
            this.destroyNode();
            gateDiscarded.dispatch(this);
        }
        return !!this.node;
    }

    refreshStyle(): void {
        super.refreshStyle();
        this.setStyle(this.styleSheet.gate);
        Object.assign(this.parentNode.style, this.computeParentStyle());
        this.panel.updateOptions({style: this.getPanelStyle()});
        this.setStyle(this.options.customStyle ?? {});
    }

    getPanelStyle(): StyleObject {
        return {
            maxWidth: "100%",
            minWidth: "100%",
            border: 0,
            height: 0,
        };
    }

    computeParentStyle(): StyleObject {
        return {};
    }

    mount(parentNode: HTMLElement) {
        this.initialParentStyle = {};
        for (const key of Array.from(parentNode.style)) {
            this.initialParentStyle[key] = parentNode.style.getPropertyValue(key);
        }

        this.parentNode = parentNode;
        if (this.options.mountType === GATE_MOUNT_TYPE.replaceChildren) {
            parentNode.innerHTML = "";
            super.mount(parentNode, 0);
        } else if (this.options.mountType === GATE_MOUNT_TYPE.firstChild) {
            super.mount(parentNode, 0);
        } else if (this.options.mountType === GATE_MOUNT_TYPE.lastChild) {
            super.mount(parentNode, parentNode.childNodes.length);
        }

        if (this.options.gateType) {
            this.node.classList.add("blink-" + this.options.gateType);
        }

        this.panel.mount(this.node);

        if (this.locksBodyScroll) {
            // Our own teardown releases the lock, since the handle is a RemoveHandle, and a discard is
            // one of our teardowns.
            this.addCleanupJob(bodyScrollLock.acquire());
        }
    }

    destroyNode(): void {
        this.panel.destroyNode();
        const revertedParentStyle: Record<string, string> = {};
        for (const key of Object.keys(this.computeParentStyle())) {
            revertedParentStyle[key] = "";
        }
        Object.assign(this.parentNode.style, revertedParentStyle, this.initialParentStyle);
        super.destroyNode();
    }

    getInteractiveHandlers(): InteractiveHandlers {
        return {
            remove: () => {
                // Nothing to say when it's already gone - a gate the buyer dismissed gets removed again
                // by whoever mounted it, once their own reason for it goes away.
                if (this.node) {
                    this.destroyNode();
                }
            },
            updatePanel: (panelOptions: Partial<NodeElementOptions>) => {
                if (!this.panel) {
                    console.error("Panel has already been destroyed.");
                    return;
                }
                this.panel.updateOptions(panelOptions);
            },
            isDiscarded: () => this.discarded,
        }
    }

    onMount(): void {
        super.onMount();
        this.attachInterval(() => this.checkStillMounted(), DISCARD_CHECK_INTERVAL_MS);
        this.panel.sizeChange.addListener(() => this.refreshStyle());
        this.panel.addSdkListener(IFrameMessages.DESTROY_PANEL, () => {
            this.destroyNode();
        });

        this.attachEventListener(document, "visibilitychange", () => {
            if (document.visibilityState === "visible") {
                this.refreshStyle();
            }
        });
    }
}
